import { supabase } from './supabase';

/**
 * MFA Service
 * Handles Multi-Factor Authentication (TOTP) using Supabase's built-in MFA
 */

export interface MfaEnrollResponse {
  id: string;
  type: string;
  totp: {
    qr_code: string; // Data URL for QR code
    secret: string; // Secret key for manual entry
    uri: string; // OTP URI
  };
}

export interface MfaFactor {
  id: string;
  friendlyName: string;
  factorType: 'totp';
  status: 'verified' | 'unverified';
  createdAt: string;
  updatedAt: string;
}

export interface MfaChallengeResponse {
  id: string;
  expiresAt: number;
}

export const mfaService = {
  /**
   * Start MFA enrollment - generates QR code and secret
   */
  async enrollMFA(): Promise<MfaEnrollResponse> {
    const { data, error } = await supabase.auth.mfa.enroll({
      factorType: 'totp',
      friendlyName: 'Authenticator App',
    });

    if (error) throw error;

    return {
      id: data.id,
      type: data.type,
      totp: {
        qr_code: data.totp.qr_code,
        secret: data.totp.secret,
        uri: data.totp.uri,
      },
    };
  },

  /**
   * Verify enrollment by checking the TOTP code
   */
  async verifyEnrollment(factorId: string, code: string): Promise<void> {
    const challenge = await supabase.auth.mfa.challenge({ factorId });

    if (challenge.error) throw challenge.error;

    const verify = await supabase.auth.mfa.verify({
      factorId,
      challengeId: challenge.data.id,
      code,
    });

    if (verify.error) throw verify.error;
  },

  /**
   * Create a challenge for MFA verification (during login)
   */
  async createChallenge(factorId: string): Promise<MfaChallengeResponse> {
    const { data, error } = await supabase.auth.mfa.challenge({ factorId });

    if (error) throw error;

    return {
      id: data.id,
      expiresAt: data.expires_at,
    };
  },

  /**
   * Verify a challenge with TOTP code (during login)
   */
  async verifyChallenge(factorId: string, challengeId: string, code: string): Promise<void> {
    const { error } = await supabase.auth.mfa.verify({
      factorId,
      challengeId,
      code,
    });

    if (error) throw error;
  },

  /**
   * List all MFA factors for the current user
   */
  async listFactors(): Promise<MfaFactor[]> {
    try {
      const { data, error } = await supabase.auth.mfa.listFactors();

      if (error) {
        // If there's no session, return empty array (user not authenticated in Supabase yet)
        if (error.message.includes('session') || error.message.includes('Auth')) {
          return [];
        }
        throw error;
      }

      return data.totp.map(
        (factor: {
          id: string;
          friendly_name?: string;
          status: 'verified' | 'unverified';
          created_at: string;
          updated_at: string;
        }) => ({
          id: factor.id,
          friendlyName: factor.friendly_name || 'Authenticator App',
          factorType: 'totp' as const,
          status: factor.status,
          createdAt: factor.created_at,
          updatedAt: factor.updated_at,
        })
      );
    } catch (_error) {
      // Return empty array for common non-error cases
      return [];
    }
  },

  /**
   * Unenroll (remove) an MFA factor
   */
  async unenrollMFA(factorId: string): Promise<void> {
    const { error } = await supabase.auth.mfa.unenroll({ factorId });

    if (error) throw error;
  },

  /**
   * Get the current assurance level (checks if MFA is required/verified)
   */
  async getAssuranceLevel(): Promise<{
    currentLevel: string | null;
    nextLevel: string | null;
    currentAuthenticationMethods: unknown[];
  }> {
    const { data, error } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();

    if (error) throw error;

    return {
      currentLevel: data.currentLevel || null,
      nextLevel: data.nextLevel || null,
      currentAuthenticationMethods: data.currentAuthenticationMethods,
    };
  },

  /**
   * Check if user has MFA enabled
   */
  async hasMfaEnabled(): Promise<boolean> {
    const factors = await this.listFactors();
    return factors.some(factor => factor.status === 'verified');
  },

  /**
   * Get the first verified MFA factor (for login)
   */
  async getVerifiedFactor(): Promise<MfaFactor | null> {
    const factors = await this.listFactors();
    return factors.find(factor => factor.status === 'verified') || null;
  },
};
